import {
  CIVILITY_LABEL,
  Civility,
  KycStatus,
  OppSource,
  WEALTH_SOURCE_LABEL,
  WEALTH_TRANCHE_LABEL,
  WealthSource,
  WealthTranche,
} from '@crm/shared';
import { useEffect, useState } from 'react';

import type { ContactCreatePayload, ContactRow, ContactScanResponse } from '../../lib/api';
import { apiClient } from '../../lib/api';

/**
 * Context attached to the prefill when the form is opened from a card scan.
 * Drives the preview header (thumbnail + confidence badge) and the visual
 * highlight on inputs the OCR populated (left border colour). The user must
 * still review and click "Create contact" — the modal is the preview screen.
 */
export type ScanContext = {
  thumbnailDataUrl: string;
  confidence: { overall: 'high' | 'medium' | 'low'; notes: string };
  /** Form-field keys the OCR pre-populated (subset of ContactCreatePayload keys + 'address_*'). */
  ocrFieldKeys: Set<string>;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (contactId: string) => void;
  /**
   * Optional initial values produced by the business card scanner — the user
   * still reviews every field before persistence.
   */
  prefill?: ContactScanResponse['fields'] | null;
  /** Scan metadata used to render the preview header (only set when prefilled by scan). */
  scanContext?: ScanContext | null;
  /**
   * When provided, the modal switches to edit-mode: the form is pre-populated
   * with the existing contact, the submit button PATCHes instead of POSTs,
   * and `onCreated` is invoked with the same id after save (so the parent
   * can refresh the detail page).
   */
  editContact?: ContactRow | null;
};

const CONFIDENCE_BADGE: Record<'high' | 'medium' | 'low', { label: string; classes: string }> = {
  high: {
    label: 'Extraction reliable',
    classes: 'bg-bg-success text-semantic-success border-semantic-success/40',
  },
  medium: {
    label: 'Please verify',
    classes: 'bg-[#FFF7E0] text-[#8B6914] border-[#E3C57A]/60',
  },
  low: {
    label: 'Low confidence — check every field',
    classes: 'bg-bg-alert text-semantic-error border-signal/40',
  },
};

const SOURCES: { value: OppSource; label: string }[] = [
  { value: 'recommendation', label: 'Referral' },
  { value: 'event', label: 'Event' },
  { value: 'intermediary', label: 'Intermediary' },
  { value: 'bank', label: 'Bank' },
  { value: 'lawyer', label: 'Lawyer / Notary' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'other', label: 'Other' },
];

const CIVS: Civility[] = ['mr', 'mrs', 'ms', 'dr', 'prof', 'other'];

export function ContactFormModal({
  open,
  onClose,
  onCreated,
  prefill,
  scanContext,
  editContact,
}: Props) {
  const isEdit = !!editContact;
  const [form, setForm] = useState<ContactCreatePayload>(() => emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touchedKeys, setTouchedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    if (editContact) setForm(mapExisting(editContact));
    else if (prefill) setForm(mapPrefill(prefill));
    else setForm(emptyForm());
    setError(null);
    setSubmitting(false);
    setTouchedKeys(new Set());
  }, [open, prefill, editContact]);

  if (!open) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError('First name and last name are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload: ContactCreatePayload = {
        ...form,
        addresses: (form.addresses ?? []).filter(
          (a) => a.street || a.postal_code || a.city || a.country,
        ),
      };
      const saved = editContact
        ? await apiClient.updateContact(editContact.id, payload)
        : await apiClient.createContact(payload);
      onCreated(saved.id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  function set<K extends keyof ContactCreatePayload>(key: K, value: ContactCreatePayload[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    if (scanContext && scanContext.ocrFieldKeys.has(key as string)) {
      setTouchedKeys((prev) => (prev.has(key as string) ? prev : new Set(prev).add(key as string)));
    }
  }

  // Mark an address sub-field touched. Address keys live under "address_*"
  // in scanContext.ocrFieldKeys (street/postal_code/city/country) — distinct
  // from top-level form keys so we don't false-positive when the user
  // reshapes the addresses array.
  function setAddressFieldTouched(addressKey: 'street' | 'postal_code' | 'city' | 'country') {
    const k = `address_${addressKey}`;
    if (!scanContext?.ocrFieldKeys.has(k)) return;
    setTouchedKeys((prev) => (prev.has(k) ? prev : new Set(prev).add(k)));
  }

  // Returns the border-left class for an input. When the field was filled
  // by OCR and the user hasn't touched it: glacier-deep blue. After edit:
  // graphite. Empty string when scan context is absent or this field
  // wasn't extracted.
  function ocrCls(key: string): string {
    if (!scanContext?.ocrFieldKeys.has(key)) return '';
    return touchedKeys.has(key)
      ? 'border-l-[3px] border-l-graphite'
      : 'border-l-[3px] border-l-[#4A5F7A]';
  }

  const badge = scanContext ? CONFIDENCE_BADGE[scanContext.confidence.overall] : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? 'Edit contact' : 'New contact'}
      className="fixed inset-0 z-40 bg-graphite/40 backdrop-blur-[2px] flex items-stretch lg:items-center justify-center"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-5xl bg-surface rounded-card shadow-panel m-4 lg:m-10 flex flex-col max-h-[calc(100vh-2rem)] lg:max-h-[calc(100vh-5rem)]"
      >
        <header className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-platinum">
          <h2 className="font-display text-xl font-semibold">
            {isEdit ? 'Edit contact' : 'New contact'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-xs uppercase tracking-[0.16em] text-ink-tertiary hover:text-ink"
          >
            Close
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
          {scanContext && badge && (
            <div className="flex items-start gap-4 rounded-card border border-platinum bg-glacier/30 px-4 py-3">
              <img
                src={scanContext.thumbnailDataUrl}
                alt="Scanned business card"
                className="w-28 h-auto max-h-24 object-contain rounded-sm border border-platinum bg-white"
              />
              <div className="flex-1 min-w-0">
                <p className="label-uppercase text-ink-tertiary">
                  OCR preview · review before saving
                </p>
                <p
                  className={
                    'mt-1 inline-flex items-center gap-2 rounded-pill border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ' +
                    badge.classes
                  }
                  role="status"
                >
                  {badge.label}
                </p>
                {scanContext.confidence.notes && (
                  <p className="mt-2 text-[12px] italic text-ink-secondary leading-relaxed">
                    {scanContext.confidence.notes}
                  </p>
                )}
                <p className="mt-2 text-[11px] text-ink-tertiary leading-relaxed">
                  Fields with a blue left border were extracted by OCR. The border turns graphite
                  once you confirm them manually.
                </p>
              </div>
            </div>
          )}

          <Section title="Identity">
            <Field label="Title">
              <select
                value={form.civility ?? ''}
                onChange={(e) => set('civility', (e.target.value || null) as Civility | null)}
                className="select-base"
              >
                <option value="">—</option>
                {CIVS.map((c) => (
                  <option key={c} value={c}>
                    {CIVILITY_LABEL[c]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="First name *">
              <input
                required
                value={form.first_name}
                onChange={(e) => set('first_name', e.target.value)}
                className={`input-base ${ocrCls('first_name')}`}
              />
            </Field>
            <Field label="Last name *">
              <input
                required
                value={form.last_name}
                onChange={(e) => set('last_name', e.target.value)}
                className={`input-base ${ocrCls('last_name')}`}
              />
            </Field>
            <Field label="Nickname">
              <input
                value={form.nickname ?? ''}
                onChange={(e) => set('nickname', e.target.value || null)}
                className="input-base"
              />
            </Field>
            <Field label="Date of birth">
              <input
                type="date"
                value={form.date_of_birth ?? ''}
                onChange={(e) => set('date_of_birth', e.target.value || null)}
                className="input-base font-tabular"
              />
            </Field>
            <Field label="Photo URL">
              <input
                value={form.photo_url ?? ''}
                onChange={(e) => set('photo_url', e.target.value || null)}
                className="input-base"
                placeholder="https://…"
              />
            </Field>
          </Section>

          <Section title="Contact details">
            <Field label="Primary email">
              <input
                type="email"
                value={form.email_primary ?? ''}
                onChange={(e) => set('email_primary', e.target.value || null)}
                className={`input-base ${ocrCls('email_primary')}`}
              />
            </Field>
            <Field label="Secondary email">
              <input
                type="email"
                value={form.email_secondary ?? ''}
                onChange={(e) => set('email_secondary', e.target.value || null)}
                className={`input-base ${ocrCls('email_secondary')}`}
              />
            </Field>
            <Field label="LinkedIn URL">
              <input
                value={form.linkedin_url ?? ''}
                onChange={(e) => set('linkedin_url', e.target.value || null)}
                className={`input-base ${ocrCls('linkedin_url')}`}
                placeholder="https://linkedin.com/in/…"
              />
            </Field>
            <Field label="Primary phone">
              <input
                value={form.phone_primary ?? ''}
                onChange={(e) => set('phone_primary', e.target.value || null)}
                className={`input-base font-tabular ${ocrCls('phone_primary')}`}
              />
            </Field>
            <Field label="Secondary phone">
              <input
                value={form.phone_secondary ?? ''}
                onChange={(e) => set('phone_secondary', e.target.value || null)}
                className={`input-base font-tabular ${ocrCls('phone_secondary')}`}
              />
            </Field>
            <Field label="Languages (ISO 639-1, comma-separated)">
              <input
                value={(form.languages ?? []).join(', ')}
                onChange={(e) =>
                  set(
                    'languages',
                    e.target.value
                      .split(',')
                      .map((s) => s.trim().toLowerCase())
                      .filter((s) => s.length === 2),
                  )
                }
                className="input-base font-tabular"
                placeholder="fr, en, it"
              />
            </Field>
          </Section>

          <Section title="Professional">
            <Field label="Company">
              <input
                value={form.company ?? ''}
                onChange={(e) => set('company', e.target.value || null)}
                className={`input-base ${ocrCls('company')}`}
              />
            </Field>
            <Field label="Position">
              <input
                value={form.position ?? ''}
                onChange={(e) => set('position', e.target.value || null)}
                className={`input-base ${ocrCls('position')}`}
              />
            </Field>
            <Field label="Industry / sector">
              <input
                value={form.industry_sector ?? ''}
                onChange={(e) => set('industry_sector', e.target.value || null)}
                className="input-base"
              />
            </Field>
          </Section>

          <Section title="Primary address">
            <Field label="Label">
              <input
                value={form.addresses?.[0]?.label ?? 'home'}
                onChange={(e) =>
                  set('addresses', [
                    { ...(form.addresses?.[0] ?? defaultAddress()), label: e.target.value },
                  ])
                }
                className="input-base"
              />
            </Field>
            <Field label="Street" full>
              <input
                value={form.addresses?.[0]?.street ?? ''}
                onChange={(e) => {
                  set('addresses', [
                    {
                      ...(form.addresses?.[0] ?? defaultAddress()),
                      street: e.target.value || null,
                    },
                  ]);
                  setAddressFieldTouched('street');
                }}
                className={`input-base ${ocrCls('address_street')}`}
              />
            </Field>
            <Field label="Postal code">
              <input
                value={form.addresses?.[0]?.postal_code ?? ''}
                onChange={(e) => {
                  set('addresses', [
                    {
                      ...(form.addresses?.[0] ?? defaultAddress()),
                      postal_code: e.target.value || null,
                    },
                  ]);
                  setAddressFieldTouched('postal_code');
                }}
                className={`input-base font-tabular ${ocrCls('address_postal_code')}`}
              />
            </Field>
            <Field label="City">
              <input
                value={form.addresses?.[0]?.city ?? ''}
                onChange={(e) => {
                  set('addresses', [
                    {
                      ...(form.addresses?.[0] ?? defaultAddress()),
                      city: e.target.value || null,
                    },
                  ]);
                  setAddressFieldTouched('city');
                }}
                className={`input-base ${ocrCls('address_city')}`}
              />
            </Field>
            <Field label="Country (ISO2)">
              <input
                maxLength={2}
                value={form.addresses?.[0]?.country ?? ''}
                onChange={(e) => {
                  set('addresses', [
                    {
                      ...(form.addresses?.[0] ?? defaultAddress()),
                      country: e.target.value ? e.target.value.toUpperCase() : null,
                    },
                  ]);
                  setAddressFieldTouched('country');
                }}
                className={`input-base uppercase font-tabular ${ocrCls('address_country')}`}
              />
            </Field>
          </Section>

          <Section title="Wealth (indicative)">
            <Field label="Wealth tranche">
              <select
                value={form.wealth_tranche ?? 'unknown'}
                onChange={(e) => set('wealth_tranche', e.target.value as WealthTranche)}
                className="select-base"
              >
                {Object.entries(WEALTH_TRANCHE_LABEL).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Source of wealth">
              <select
                value={form.wealth_source ?? 'unknown'}
                onChange={(e) => set('wealth_source', e.target.value as WealthSource)}
                className="select-base"
              >
                {Object.entries(WEALTH_SOURCE_LABEL).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Tax residence (ISO2)">
              <input
                maxLength={2}
                value={form.tax_residence ?? ''}
                onChange={(e) =>
                  set('tax_residence', e.target.value ? e.target.value.toUpperCase() : null)
                }
                className="input-base uppercase font-tabular"
              />
            </Field>
            <Field label="Nationalities (ISO2, comma-separated)">
              <input
                value={(form.nationalities ?? []).join(', ')}
                onChange={(e) =>
                  set(
                    'nationalities',
                    e.target.value
                      .split(',')
                      .map((s) => s.trim().toUpperCase())
                      .filter((s) => s.length === 2),
                  )
                }
                className="input-base uppercase font-tabular"
                placeholder="CH, IT"
              />
            </Field>
          </Section>

          <Section title="KYC">
            <Field label="KYC status">
              <select
                value={form.kyc_status ?? 'pending'}
                onChange={(e) => set('kyc_status', e.target.value as KycStatus)}
                className="select-base"
              >
                <option value="pending">Pending</option>
                <option value="in_progress">In progress</option>
                <option value="complete">Complete</option>
                <option value="expired">Expired</option>
              </select>
            </Field>
            <Field label="Last KYC review">
              <input
                type="date"
                value={form.kyc_last_review_date ?? ''}
                onChange={(e) => set('kyc_last_review_date', e.target.value || null)}
                className="input-base font-tabular"
              />
            </Field>
            <Field label="ID document type">
              <input
                value={form.id_document_type ?? ''}
                onChange={(e) => set('id_document_type', e.target.value || null)}
                placeholder="passport / id_card / driver_license"
                className="input-base"
              />
            </Field>
            <Field label="ID document (masked)">
              <input
                value={form.id_document_masked ?? ''}
                onChange={(e) => set('id_document_masked', e.target.value || null)}
                className="input-base font-tabular"
                placeholder="••••1234"
              />
            </Field>
            <Field label="ID expiry">
              <input
                type="date"
                value={form.id_document_expiry ?? ''}
                onChange={(e) => set('id_document_expiry', e.target.value || null)}
                className="input-base font-tabular"
              />
            </Field>
            <Field label="PEP flag">
              <label className="flex items-center gap-2 mt-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.pep_flag ?? false}
                  onChange={(e) => set('pep_flag', e.target.checked)}
                />
                Politically Exposed Person
              </label>
            </Field>
          </Section>

          <Section title="Origin &amp; relation">
            <Field label="Source">
              <select
                value={form.source ?? 'other'}
                onChange={(e) => set('source', e.target.value as OppSource)}
                className="select-base"
              >
                {SOURCES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="First met on">
              <input
                type="date"
                value={form.first_met_at ?? ''}
                onChange={(e) => set('first_met_at', e.target.value || null)}
                className="input-base font-tabular"
              />
            </Field>
            <Field label="Tags (comma-separated)">
              <input
                value={(form.tags ?? []).join(', ')}
                onChange={(e) =>
                  set(
                    'tags',
                    e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  )
                }
                className="input-base"
                placeholder="vip, family-office, geneva"
              />
            </Field>
            <Field label="Notes" full>
              <textarea
                rows={3}
                value={form.notes ?? ''}
                onChange={(e) => set('notes', e.target.value || null)}
                className="input-base"
              />
            </Field>
          </Section>

          {error && (
            <p className="text-sm text-semantic-error border border-signal/30 bg-bg-alert px-3 py-2 rounded-card">
              {error}
            </p>
          )}
        </div>

        <footer className="shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-platinum">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60">
            {submitting ? 'Saving…' : isEdit ? 'Save' : 'Create contact'}
          </button>
        </footer>
      </form>
    </div>
  );
}

function emptyForm(): ContactCreatePayload {
  return {
    first_name: '',
    last_name: '',
    civility: null,
    nickname: null,
    photo_url: null,
    email_primary: null,
    email_secondary: null,
    phone_primary: null,
    phone_secondary: null,
    linkedin_url: null,
    company: null,
    position: null,
    industry_sector: null,
    date_of_birth: null,
    nationalities: [],
    tax_residence: null,
    languages: [],
    wealth_tranche: 'unknown',
    wealth_source: 'unknown',
    pep_flag: false,
    id_document_type: null,
    id_document_masked: null,
    id_document_expiry: null,
    kyc_last_review_date: null,
    kyc_status: 'pending',
    source: 'other',
    referred_by_contact: null,
    first_met_at: null,
    owner_rm_id: null,
    tags: [],
    notes: null,
    addresses: [defaultAddress()],
  };
}

function defaultAddress() {
  return {
    label: 'home',
    street: null,
    postal_code: null,
    city: null,
    country: null,
    is_primary: true,
  };
}

function mapExisting(c: ContactRow): ContactCreatePayload {
  // Map a fully serialized contact (from the API) back to the create-payload
  // shape used by the form. Addresses are passed through verbatim with the
  // server-assigned ids stripped (PATCH replaces them wholesale anyway).
  return {
    civility: c.civility,
    first_name: c.first_name,
    last_name: c.last_name,
    nickname: c.nickname,
    photo_url: c.photo_url,
    email_primary: c.email_primary,
    email_secondary: c.email_secondary,
    phone_primary: c.phone_primary,
    phone_secondary: c.phone_secondary,
    linkedin_url: c.linkedin_url,
    company: c.company,
    position: c.position,
    industry_sector: c.industry_sector,
    date_of_birth: c.date_of_birth,
    nationalities: c.nationalities,
    tax_residence: c.tax_residence,
    languages: c.languages,
    wealth_tranche: c.wealth_tranche,
    wealth_source: c.wealth_source,
    pep_flag: c.pep_flag,
    id_document_type: c.id_document_type,
    id_document_masked: c.id_document_masked,
    id_document_expiry: c.id_document_expiry,
    kyc_last_review_date: c.kyc_last_review_date,
    kyc_status: c.kyc_status,
    source: c.source,
    referred_by_contact: c.referred_by_contact,
    first_met_at: c.first_met_at,
    owner_rm_id: c.owner_rm_id,
    tags: c.tags,
    notes: c.notes,
    addresses: c.addresses.length
      ? c.addresses.map((a) => ({
          label: a.label,
          street: a.street,
          postal_code: a.postal_code,
          city: a.city,
          country: a.country,
          is_primary: a.is_primary,
        }))
      : [defaultAddress()],
  };
}

function mapPrefill(p: ContactScanResponse['fields']): ContactCreatePayload {
  // Flatten the rich tool_use output into the form's flat shape. Order of
  // arrays mirrors visual prominence on the card, so the first email/phone
  // is the most likely primary contact.
  const primaryPhone = p.phones[0];
  const secondaryPhone = p.phones[1];
  const base = emptyForm();
  return {
    ...base,
    first_name: p.firstName ?? '',
    last_name: p.lastName ?? '',
    company: p.company ?? null,
    position: p.title ?? null,
    email_primary: p.emails[0] ?? null,
    email_secondary: p.emails[1] ?? null,
    phone_primary: primaryPhone ? (primaryPhone.e164 ?? primaryPhone.raw) : null,
    phone_secondary: secondaryPhone ? (secondaryPhone.e164 ?? secondaryPhone.raw) : null,
    linkedin_url: p.linkedinUrl ?? null,
    addresses: [
      {
        label: 'office',
        street: p.addressLine ?? null,
        postal_code: p.postalCode ?? null,
        city: p.city ?? null,
        country: p.country ? p.country.slice(0, 2).toUpperCase() : null,
        is_primary: true,
      },
    ],
  };
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset>
      <legend className="label-uppercase mb-3">{title}</legend>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
        {children}
      </div>
    </fieldset>
  );
}

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <label className={'block text-sm ' + (full ? 'md:col-span-2' : '')}>
      <span className="block text-xs uppercase tracking-wider text-ink-secondary mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}
